import { jsx as _jsx } from "react/jsx-runtime";
/**
 * WE2 P1-02: VNInvestChartsContext (main provider)
 *
 * Composes ConfigContext, StateContext, WidgetI18nContext, and HoverEmitter
 * into a single provider tree. This is the root context for the compound
 * component architecture.
 *
 * The provider wraps around the existing VNInvestCharts implementation — it does
 * NOT replace the prop-driven API. Instead, it provides an internal coordination
 * layer that compound sub-components (Toolbar, Header, Footer, etc.) can consume.
 *
 * Architecture:
 *   VNInvestChartsProvider
 *     ├── WidgetI18nProvider (already exists)
 *     ├── VNInvestChartsConfigProvider (theme, locale, symbol, timeframe, adapter)
 *     ├── VNInvestChartsStateProvider (panes, drawings, activeTool)
 *     └── HoverEmitter ref (high-frequency hover events via EventTarget)
 */
import { createContext, useContext, useRef } from "react";
import { WidgetI18nProvider } from "./WidgetI18nContext";
import { VNInvestChartsConfigProvider } from "./VNInvestChartsConfigContext";
import { VNInvestChartsStateProvider } from "./VNInvestChartsStateContext";
import { createHoverEmitter } from "./HoverEmitter";
/** Context for accessing the internal portal root (for Shadow DOM isolation) */
const PortalRootContext = createContext(null);
/**
 * Hook to access the internal portal root for rendering modals/tooltips inside Shadow DOM.
 * Returns the portal root element, or null if not in Shadow DOM mode.
 */
export function usePortalRoot() {
    return useContext(PortalRootContext);
}
/** Context for accessing the shadow root (for event listener registration in Shadow DOM) */
const ShadowRootContext = createContext(null);
/** Context for accessing the hover emitter ref (high-frequency hover events via EventTarget) */
const HoverEmitterContext = createContext({ current: null });
/**
 * Hook to access the shadow root for registering capture-phase event listeners.
 * Returns the shadow root element, or null if not in Shadow DOM mode.
 */
export function useShadowRoot() {
    return useContext(ShadowRootContext);
}
/**
 * Hook to access the hover emitter ref from any descendant component.
 *
 * Usage:
 *   const emitterRef = useHoverEmitterRef();
 *   emitHover(emitterRef.current, { datum, pixelX, pixelY, paneId });
 */
export function useHoverEmitterRef() {
    return useContext(HoverEmitterContext);
}
/**
 * Root provider for the compound component architecture.
 *
 * Wraps the existing VNInvestCharts prop-driven implementation — does NOT replace it.
 * Compound sub-components (Toolbar, Header, Footer, etc.) consume this context.
 */
export function VNInvestChartsProvider({ adapter, symbol, timeframe, locale, messages, theme, panes, drawings, activeTool, storageScopeId, portalRoot, shadowRoot, onLocaleChange, children, }) {
    const hoverEmitterRef = useRef(createHoverEmitter());
    const resolvedTheme = theme ?? "modern-light";
    const resolvedLocale = locale ?? "vi";
    return (_jsx(WidgetI18nProvider, { locale: resolvedLocale, messages: messages, onLocaleChange: onLocaleChange, children: _jsx(VNInvestChartsConfigProvider, { value: {
                theme: resolvedTheme,
                locale: resolvedLocale,
                symbol,
                timeframe,
                adapter,
            }, children: _jsx(VNInvestChartsStateProvider, { initialPanes: panes, initialDrawings: drawings, initialActiveTool: activeTool, storageScopeId: storageScopeId, children: _jsx(PortalRootContext.Provider, { value: portalRoot ?? null, children: _jsx(ShadowRootContext.Provider, { value: shadowRoot ?? null, children: _jsx(HoverEmitterContext.Provider, { value: hoverEmitterRef, children: children }) }) }) }) }) }));
}
export { VNInvestChartsConfigContext, VNInvestChartsConfigProvider } from "./VNInvestChartsConfigContext";
export { VNInvestChartsStateContext, VNInvestChartsStateProvider, useVNInvestChartsState } from "./VNInvestChartsStateContext";
export { createHoverEmitter, emitHover, clearHover, useHoverSubscription, useHoverState, } from "./HoverEmitter";
